import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { HelpCircle, AlertTriangle } from "lucide-react";

/**
 * Fase 8.8 · Popover "Perché non tutti pubblici?"
 *
 * Espone il breakdown live dei prodotti nascosti al pubblico per causa
 * primaria, con opzione di click per filtrare la lista.
 *
 * Props:
 *  - categoria: "vino" | "non_vino" | "distillato" | "liquore" | "birra" | "altro"
 *  - onJumpToFilter?: (filterKey: "stock_zero"|"not_normalized"|"pending_review"|"hidden_from_public") => void
 */
const REASON_LABEL = {
    stock_zero_all_formats: {
        label: "Giacenza zero su tutti i formati",
        hint: "Auto-nascosti finché la giacenza non torna > 0 (o override manuale).",
        filter: "stock_zero",
    },
    not_normalized: {
        label: "Non ancora normalizzati dall'AI",
        hint: "Vini Mexal in attesa di normalizzazione (nome, produttore, tipologia). Coda AI in corso.",
        filter: "not_normalized",
    },
    pending_review: {
        label: "In attesa di revisione",
        hint: "Creati manualmente / da foto / da import: aspettano approvazione admin.",
        filter: "pending_review",
    },
    force_hidden_manual: {
        label: "Nascosti manualmente",
        hint: "L'admin ha impostato visibilità = Nascosto per questi prodotti.",
        filter: "hidden_from_public",
    },
    name_raw_mexal: {
        label: "Descrizione grezza Mexal",
        hint: "Il nome è ancora la stringa grezza di Mexal (non normalizzata).",
        filter: "not_normalized",
    },
    producer_invalid: {
        label: "Produttore non valido",
        hint: "Manca un produttore reale (stringa vuota, trattino…).",
        filter: null,
    },
    other: {
        label: "Altre cause",
        hint: "Casi residui — cliccare sui filtri esistenti per approfondire.",
        filter: null,
    },
};

export default function VisibilityBreakdownPopover({ categoria, onJumpToFilter }) {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!open) return;
        const params = categoria ? { categoria } : {};
        api.get("/admin/visibility-breakdown", { params })
            .then(({ data }) => setData(data))
            .catch(() => setData({ error: true }));
    }, [open, categoria]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="inline-flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] underline underline-offset-2 decoration-dotted"
                    data-testid="visibility-breakdown-trigger"
                    title="Perché non tutti pubblici?"
                >
                    <HelpCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Perché non tutti?
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-96 p-4" data-testid="visibility-breakdown-panel">
                <div className="text-sm font-medium mb-3">Perché non tutti sono pubblici</div>
                {!data && <div className="text-xs text-[hsl(var(--muted-foreground))]">Caricamento…</div>}
                {data && data.error && (
                    <div className="text-xs text-[hsl(var(--destructive))] flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5" />
                        Impossibile calcolare il breakdown.
                    </div>
                )}
                {data && !data.error && (
                    <div className="space-y-3">
                        <div className="text-xs text-[hsl(var(--muted-foreground))]">
                            <b className="text-[hsl(var(--foreground))]">{data.public}</b> pubblici · <b className="text-[hsl(var(--foreground))]">{data.hidden}</b> nascosti su {data.total} totali.
                            Le cause sono disgiunte (ogni prodotto conta una sola volta).
                        </div>
                        <ul className="space-y-1">
                            {Object.entries(data.reasons)
                                .filter(([, v]) => v > 0)
                                .sort(([, a], [, b]) => b - a)
                                .map(([k, v]) => {
                                    const meta = REASON_LABEL[k] || { label: k, hint: "", filter: null };
                                    const clickable = meta.filter && onJumpToFilter;
                                    return (
                                        <li
                                            key={k}
                                            className={`flex items-start justify-between gap-3 p-2 rounded-sm ${clickable ? "cursor-pointer hover:bg-[#FDFBF7]" : ""}`}
                                            onClick={() => {
                                                if (clickable) {
                                                    onJumpToFilter(meta.filter);
                                                    setOpen(false);
                                                }
                                            }}
                                            data-testid={`breakdown-reason-${k}`}
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm">
                                                    {meta.label}
                                                    {clickable && (
                                                        <span className="ml-2 text-[10px] uppercase tracking-wider text-[hsl(var(--primary))]">
                                                            filtra
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-[11px] text-[hsl(var(--muted-foreground))]">
                                                    {meta.hint}
                                                </div>
                                            </div>
                                            <div className="text-lg tabular-nums shrink-0 text-[hsl(var(--foreground))]">
                                                {v}
                                            </div>
                                        </li>
                                    );
                                })}
                        </ul>
                        {Object.values(data.reasons).every((v) => v === 0) && (
                            <div className="text-xs text-[hsl(var(--muted-foreground))]">
                                Tutti i prodotti sono pubblici.
                            </div>
                        )}
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}
